// src/pages/Automations/components/TemplateBasicsModal.jsx
// Modal for editing global template variables (Light theme)

import React, { useState, useEffect } from 'react';
import { X, User, Phone, Mail, Building, Clock, Link, Info, Check, Loader2 } from 'lucide-react';

export function TemplateBasicsModal({ isOpen, onClose, basics, onSave, saving, proirpPhone }) {
  const [form, setForm] = useState({
    agent_name: '',
    agent_phone: '',
    agent_email: '',
    agency_name: '',
    office_hours: '',
    appointment_link: ''
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && basics) {
      setForm({
        agent_name: basics.agent_name || '',
        agent_phone: basics.agent_phone || '',
        agent_email: basics.agent_email || '',
        agency_name: basics.agency_name || '',
        office_hours: basics.office_hours || '',
        appointment_link: basics.appointment_link || ''
      });
      setShowSuccess(false);
      setError(null);
    }
  }, [isOpen, basics]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const success = await onSave(form);
    if (success) {
      setShowSuccess(true);
      // Close after showing success briefly
      setTimeout(() => {
        onClose();
        setShowSuccess(false);
      }, 1500);
    } else {
      setError('Failed to save. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Template Basics</h2>
            <p className="text-sm text-gray-500 mt-1">
              These values auto-fill all your templates
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form id="template-basics-form" onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Agent Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <User className="w-4 h-4" />
              Agent Name
            </label>
            <input
              type="text"
              value={form.agent_name}
              onChange={(e) => handleChange('agent_name', e.target.value)}
              placeholder="John Smith"
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>

          {/* Agent Phone */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Phone className="w-4 h-4" />
              Agent Phone
            </label>
            <div className="flex gap-2">
              <input
                type="tel"
                value={form.agent_phone}
                onChange={(e) => handleChange('agent_phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="flex-1 px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
              {proirpPhone && form.agent_phone !== proirpPhone && (
                <button
                  type="button"
                  onClick={() => handleChange('agent_phone', proirpPhone)}
                  className="px-3 py-2 text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors whitespace-nowrap"
                  title={`Use your Pro IRP number: ${proirpPhone}`}
                >
                  Use Pro IRP #
                </button>
              )}
            </div>
            {proirpPhone && (
              <p className="text-xs text-gray-500 mt-1">
                Pro IRP number: <span className="font-medium text-blue-600">{proirpPhone}</span>
              </p>
            )}
          </div>

          {/* Agent Email */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Mail className="w-4 h-4" />
              Agent Email
            </label>
            <input
              type="email"
              value={form.agent_email}
              onChange={(e) => handleChange('agent_email', e.target.value)}
              placeholder="john@medicare.agency"
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>

          {/* Agency Name */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Building className="w-4 h-4" />
              Agency Name
            </label>
            <input
              type="text"
              value={form.agency_name}
              onChange={(e) => handleChange('agency_name', e.target.value)}
              placeholder="Smith Medicare Services"
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>

          {/* Office Hours */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Clock className="w-4 h-4" />
              Office Hours
            </label>
            <input
              type="text"
              value={form.office_hours}
              onChange={(e) => handleChange('office_hours', e.target.value)}
              placeholder="Mon-Fri 9am-5pm"
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>

          {/* Appointment Link */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Link className="w-4 h-4" />
              Appointment Link
            </label>
            <input
              type="url"
              value={form.appointment_link}
              onChange={(e) => handleChange('appointment_link', e.target.value)}
              placeholder="https://calendly.com/yourname"
              className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
          </div>

          {/* Info Box */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl mt-6">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="font-medium text-blue-700 mb-1">How it works</p>
              <p>Use variables like <code className="px-1 py-0.5 bg-white rounded text-amber-600 border">{'{AgentName}'}</code> in your templates. They'll automatically be replaced with your info.</p>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200">
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Success Message */}
          {showSuccess ? (
            <div className="flex items-center justify-center gap-3 py-2">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-green-700 font-semibold">Saved successfully!</span>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="template-basics-form"
                disabled={saving}
                className="px-6 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TemplateBasicsModal;
